import { formatDate } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import {
  AnhangDtoModel,
  AntragDtoModel,
  AntragsInfoExtended,
  AntragStatusType,
  RemoteAntragStatus,
  TokenState,
} from '../models';
import { A11YFormatService, ConfigSettingsService, ResourceProvider } from '../providers';
import { AntragSupportingDataService } from './antrag-supporting-data.service';

@Injectable({
  providedIn: 'root',
})
export class AntragHelperService {
  private readonly a11yFormatService = inject(A11YFormatService);
  private readonly configService = inject(ConfigSettingsService);
  private readonly resourceProvider = inject(ResourceProvider);
  private readonly antragSupportingDataService = inject(AntragSupportingDataService);
  private readonly translateService = inject(TranslateService);

  private readonly antragsHistory$ = this.resourceProvider
    .getAntragsHistory(true)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public getStateI18nKey(label: string): string {
    return `antrags_uebersicht.server.antrags_status.${label}`;
  }

  public canDeleteAntrag(status: RemoteAntragStatus): boolean {
    return status.isDraft;
  }

  public hasDifferentToken(status: TokenState): Observable<boolean> {
    return this.antragSupportingDataService.hasEncryptionIssue(status);
  }

  public sortAntragSeiten(antragsseiteInfo: AnhangDtoModel[]): AnhangDtoModel[][] {
    const belegeMap = new Map<number, AnhangDtoModel[]>();

    for (const seite of antragsseiteInfo) {
      const entry = belegeMap.get(seite.belegNr) ?? [];
      entry.push(seite);
      belegeMap.set(seite.belegNr, entry);
    }

    return [...belegeMap.entries()]
      .toSorted(([a], [b]) => a - b)
      .map(([, seiten]) => seiten.toSorted((a, b) => a.seitenNr - b.seitenNr));
  }

  public filterForDifferentEinreichungsVersions(antrag: AntragDtoModel): boolean {
    const einreichungV3Activated = this.configService.appSettings.einreichungV3Activated;
    const allowPdfUpload = this.configService.appSettings.customizing.allowPdfUpload;

    if (!einreichungV3Activated && !allowPdfUpload) {
      return this.hasPdfPage(antrag);
    }

    return false;
  }

  public getDraftCount(): Promise<number> {
    return firstValueFrom(this.filterAndCountAntrage((s) => s.isDraft));
  }

  public getFinishedAntragCount(): Promise<number> {
    return firstValueFrom(this.filterAndCountAntrage((s) => s.isFinal));
  }

  public async turnIntoAntragsInfoExtended(antrag: AntragDtoModel): Promise<AntragsInfoExtended> {
    const isAntragDeletable = this.canDeleteAntrag(antrag.status);
    const cannotDecrypt = await firstValueFrom(this.hasDifferentToken(antrag.tokenState));
    const antragsSize = (antrag.seitenBytesTotal / 1024 / 1024).toFixed(2);
    const hasDedicatedFirst = antrag.flags?.includes('HasDedicatedFirst') ?? false;
    const statusType = this.getStatusType(antrag.status);

    return {
      ...antrag,
      hasDedicatedFirst,
      belege: this.sortAntragSeiten(antrag.antragsseiteInfo),
      canDeleteAntrag: isAntragDeletable,
      cannotDecrypt,
      antragsSize,
      statusTypeCss: statusType,
      statusTypeIcon: statusType,
      title: this.getTitle(antrag),
      titleDate: this.getTitleDate(antrag),
      titleBelege: this.getTitleBelege(antrag, cannotDecrypt, hasDedicatedFirst, antragsSize),
      titleTotalA11y: `${this.getTitle(antrag, true)} ${this.getTitleBelege(antrag, cannotDecrypt, hasDedicatedFirst, antragsSize, true)}`,
      isLangantrag: antrag.isLangantrag,
      isFeatureDedicatedBelegEnabledOnAntrag: antrag.isFeatureDedicatedBelegEnabledOnAntrag,
    };
  }

  private getStatusType(status: RemoteAntragStatus): AntragStatusType {
    if (status.isFinal) {
      return 'succeeded';
    }
    if (status.isError) {
      return 'errored';
    }
    return 'waiting';
  }

  private hasPdfPage(antrag: AntragDtoModel): boolean {
    return antrag.antragsseiteInfo.some((seite) => {
      return seite.dataType === 'pdf';
    });
  }

  private filterAndCountAntrage(predicate: (status: RemoteAntragStatus) => boolean): Observable<number> {
    return this.antragsHistory$.pipe(
      map(
        (antraege) =>
          antraege.filter((antrag) => predicate(antrag.status) && !this.filterForDifferentEinreichungsVersions(antrag))
            .length,
      ),
    );
  }

  private getTitle(antrag: AntragDtoModel, a11yMode = false): string {
    if (this.isEinreichungV3Title(antrag)) {
      return this.getEinreichungV3Title(antrag, a11yMode);
    }

    if (antrag.status.isDraft) {
      return this.getDraftTitle(antrag, a11yMode);
    }

    return this.getDefaultTitle(antrag, a11yMode);
  }

  private isEinreichungV3Title(antrag: AntragDtoModel): boolean {
    return this.configService.appSettings.einreichungV3Activated === true && !!antrag.containerName;
  }

  private getEinreichungV3Title(antrag: AntragDtoModel, a11yMode: boolean): string {
    const date = a11yMode ? this.a11yFormatService.dateToA11yString(antrag.hochgeladen, 'd m Y') : '';

    return `${antrag.containerName} ${date}`.trim();
  }

  private getDraftTitle(antrag: AntragDtoModel, a11yMode: boolean): string {
    const { hasUnfinishedAntrage } = this.configService.appSettings.customizing;

    const labelKey = hasUnfinishedAntrage ? _('antrags_uebersicht.label.draft') : _('antrags_uebersicht.label.failing');

    return this.translateService.instant(labelKey, {
      date: this.formatDate(antrag.concurrency, a11yMode),
    });
  }

  private getDefaultTitle(antrag: AntragDtoModel, a11yMode: boolean): string {
    return this.translateService.instant(a11yMode ? 'a11y.antrag.title' : 'antrags_uebersicht.label.title', {
      date: this.formatDate(antrag.hochgeladen, a11yMode),
    });
  }
  private formatDate(date: Date | string | null | undefined, a11yMode: boolean): string {
    if (!date) {
      return '';
    }

    const parsedDate = typeof date === 'string' ? new Date(date) : date;

    return a11yMode
      ? this.a11yFormatService.dateToA11yString(parsedDate, 'd m Y')
      : formatDate(parsedDate, 'shortDate', 'de');
  }

  private getTitleDate(antrag: AntragDtoModel): string {
    const label = antrag.status.isDraft ? 'title_created' : 'title_submitted';
    const date = antrag.hochgeladen || antrag.angelegt;

    return this.translateService.instant(_(`antrags_uebersicht.label.${label}`), {
      date: formatDate(date, 'shortDate', 'de'),
    });
  }

  private getTitleBelege(
    antrag: AntragDtoModel,
    cannotDecrypt: boolean,
    hasDedicatedFirst: boolean,
    antragsSize: string,
    a11yMode = false,
  ): string {
    if (cannotDecrypt) {
      return this.translateService.instant(_('antrags_uebersicht.label.auto_decrypting'));
    }

    if (hasDedicatedFirst) {
      const a11yTitle = _('a11y.antrag.has_dedicated_first');
      const title = _('antrags_uebersicht.label.has_dedicated_first');

      return this.translateService.instant(`${a11yMode ? a11yTitle : title}`, {
        numberOfBelege: antrag.anzBelege - 1,
        numberOfSeiten: antrag.anzSeiten,
        sizeInMB: a11yMode ? this.a11yFormatService.numberToA11yString(antragsSize) : antragsSize,
      });
    }

    const a11yTitle = _('a11y.antrag.title_count');
    const title = _('antrags_uebersicht.label.title_count');

    return this.translateService.instant(`${a11yMode ? a11yTitle : title}`, {
      numberOfBelege: antrag.anzBelege,
      numberOfSeiten: antrag.anzSeiten,
      sizeInMB: a11yMode ? this.a11yFormatService.numberToA11yString(antragsSize) : antragsSize,
    });
  }
}
